<template>
  <div :class="{ desktop: $isDesktop }">
    <campsite-address-and-map
      :campsite="mapCampsite"
      :campsite-address="campsite.fullAddress2"
      class="campsite-directions"
    >
      <campsite-directions v-if="$isDesktop" :campsite="campsite" />
    </campsite-address-and-map>

    <campsite-directions v-if="!$isDesktop" :campsite="campsite" />

    <section
      v-if="showNearestTransport"
      class="location-subsection nearby-transport"
    >
      <translate tag="h3"> Nearest transport </translate>
      <nearby-transport
        v-for="station in stationTypes"
        :key="station.title"
        :nearby-transport="station.data"
        :station-type="station.type"
        :title="station.title"
      />
    </section>

    <distances-note />
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { CampsiteDetails } from '~/apps/campsite-details/types'
import { MapCampsite } from '~/apps/maps/domain/MapsCampsite'
import { campsiteToMapCampsite } from '../../maps'

@Component
class CampsiteLocation extends Vue {
  @Prop()
  campsite: CampsiteDetails

  get stationTypes() {
    const stations = [
      { type: 'coach', title: this.$t('Coach') },
      { type: 'rail', title: this.$t('Rail') },
      { type: 'ferry', title: this.$t('Ferry') },
      { type: 'airport', title: this.$t('Airport') },
    ]
    const nearbyTransport = this.campsite.nearbyTransport
    return stations.map((station) => ({
      ...station,
      data: nearbyTransport ? nearbyTransport[station.type] : [],
    }))
  }

  get showNearestTransport() {
    if (!this.campsite.hierarchy) {
      return false
    }
    return ['england', 'scotland', 'wales'].includes(
      this.campsite.hierarchy.country.toLowerCase(),
    )
  }

  get mapCampsite(): MapCampsite {
    return campsiteToMapCampsite(this.campsite)
  }
}

export default toNative(CampsiteLocation)
</script>

<style scoped>
section {
  margin-bottom: 1em;
}

.location-subsection {
  font-size: var(--body);
}

.campsite-directions {
  display: flex;
  margin: 0 0 2em 0;
  padding: 0;
}

.campsite-directions :deep(.map-holder .leaflet-container) {
  height: 100% !important;
}

.campsite-directions :deep(.map-holder) {
  border-radius: 0 0 0 6em;
  display: block;
  flex-shrink: 0;
  margin: 0;
  min-height: 60vw;
  overflow: hidden;
  position: relative;
  transform: translateY(-4em);
  transition: all 0.25s ease-out 100ms;
}

.show-checkbox:checked
  ~ .subsection-content
  .campsite-directions
  :deep(.map-holder) {
  border-radius: 1em 0 0 1em;
  transform: translateY(0);
}

.campsite-directions :deep(.location-details) {
  align-self: flex-start;
  flex-shrink: 0;
  width: calc(50vw - 1.4em);
  padding-left: 0;
  padding-top: 0.25em;
}

.campsite-directions :deep(.map-holder .leaflet-control-attribution) {
  display: none;
}

.show-checkbox:checked
  ~ .subsection-content
  .campsite-directions
  :deep(.map-holder .leaflet-control-attribution) {
  display: block;
}

/* desktop & tablet */
.desktop .campsite-directions {
  background: var(--colour-page-background-light);
  flex-direction: row-reverse;
  border-radius: 0.5em;
  overflow: hidden;
}

.desktop .campsite-directions :deep(.map-holder .leaflet-container) {
  height: 100% !important;
}

.desktop .campsite-directions :deep(.location-details) {
  width: auto;
  padding: 2em;
  flex-shrink: 1;
}

.desktop .campsite-directions :deep(.location-details .button) {
  margin-bottom: 1em;
  font-size: inherit;
}

.desktop .campsite-directions :deep(.map-holder) {
  border-radius: 0;
  min-height: 24em;
  height: auto;
  transform: none;
  width: 66%;
}

.desktop .location-subsection {
  font-size: inherit;
}

.desktop
  .show-checkbox:checked
  ~ .subsection-content
  .campsite-directions
  :deep(.map-holder) {
  border-radius: 0;
}

.desktop .nearby-transport {
  column-gap: 4em;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20%, 1fr));
  grid-template-rows: auto;
  grid-auto-flow: column;
  position: relative;
  padding-top: 2em;
}

.desktop .nearby-transport :deep(h3) {
  position: absolute;
}
</style>
